'use client';

import React, { useState } from 'react';
import { Bell, MessageSquare, ChevronDown, Globe, User, Settings, LogOut, Store, Wallet, Menu,  } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const stores = [
  { id: 'store-001', name: 'Ahmed Electronics', score: 87 },
  { id: 'store-002', name: 'Fatima Fabrics', score: 72 },
];

const languages = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ur', label: 'اردو', dir: 'rtl' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
];

interface TopbarProps {
  onMobileMenuToggle?: () => void;
}

export default function Topbar({ onMobileMenuToggle }: TopbarProps) {
  const [storeOpen, setStoreOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(stores[0]);
  const [selectedLang, setSelectedLang] = useState(languages[0]);

  return (
    <header className="fixed top-0 right-0 left-0 h-14 bg-card border-b border-border z-20 flex items-center px-4 gap-3 shadow-topbar">
      {/* Mobile menu */}
      <button
        onClick={onMobileMenuToggle}
        className="lg:hidden btn-ghost p-2"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Logo mobile */}
      <div className="lg:hidden flex items-center gap-2">
        <AppLogo size={28} />
        <span className="font-bold text-sm text-primary">Arain7Seller</span>
      </div>

      {/* Store selector */}
      <div className="relative hidden lg:block">
        <button
          onClick={() => { setStoreOpen(!storeOpen); setLangOpen(false); setAvatarOpen(false); }}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-muted/50 hover:bg-muted transition-colors text-sm font-medium"
        >
          <Store size={15} className="text-primary" />
          <span className="max-w-[140px] truncate">{selectedStore.name}</span>
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
            selectedStore.score >= 90 ? 'bg-success/10 text-success' :
            selectedStore.score >= 80 ? 'bg-blue-50 text-blue-600' :
            selectedStore.score >= 70 ? 'bg-warning/10 text-warning': 'bg-danger/10 text-danger'
          }`}>
            {selectedStore.score}%
          </span>
          <ChevronDown size={14} />
        </button>

        {storeOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-xl shadow-panel py-1 fade-in">
            {stores.map((store) => (
              <button
                key={`store-${store.id}`}
                onClick={() => { setSelectedStore(store); setStoreOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted text-sm text-left transition-colors"
              >
                <Store size={14} className="text-muted-foreground" />
                <span className="flex-1 truncate">{store.name}</span>
                <span className={`text-xs font-semibold ${
                  store.score >= 80 ? 'text-success' : 'text-warning'
                }`}>{store.score}%</span>
              </button>
            ))}
            <div className="border-t border-border mt-1 pt-1">
              <button className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-xs text-primary font-medium">
                + Add New Store
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1" />

      {/* Language switcher */}
      <div className="relative">
        <button
          onClick={() => { setLangOpen(!langOpen); setStoreOpen(false); setAvatarOpen(false); }}
          className="flex items-center gap-1.5 btn-ghost px-2 py-1.5"
        >
          <Globe size={16} />
          <span className="text-xs font-medium hidden sm:block">{selectedLang.code.toUpperCase()}</span>
          <ChevronDown size={12} />
        </button>

        {langOpen && (
          <div className="absolute top-full right-0 mt-1 w-40 bg-card border border-border rounded-xl shadow-panel py-1 fade-in">
            {languages.map((lang) => (
              <button
                key={`lang-${lang.code}`}
                onClick={() => { setSelectedLang(lang); setLangOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                  selectedLang.code === lang.code ? 'text-primary font-semibold' : 'text-foreground'
                }`}
              >
                <span>{lang.label}</span>
                {lang.dir === 'rtl' && (
                  <span className="ml-auto text-2xs text-muted-foreground">RTL</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Notification bell */}
      <button className="relative btn-ghost p-2" aria-label="Notifications">
        <Bell size={18} />
        <span className="absolute top-1 right-1 w-4 h-4 bg-danger text-white text-2xs font-bold rounded-full flex items-center justify-center">
          12
        </span>
      </button>

      {/* Chat */}
      <button className="relative btn-ghost p-2" aria-label="Messages">
        <MessageSquare size={18} />
        <span className="absolute top-1 right-1 w-4 h-4 bg-accent text-white text-2xs font-bold rounded-full flex items-center justify-center">
          7
        </span>
      </button>

      {/* Wallet balance */}
      <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-success/10 rounded-lg">
        <Wallet size={14} className="text-success" />
        <span className="text-xs font-bold text-success tabular-nums">PKR 24,850</span>
      </div>

      {/* Avatar dropdown */}
      <div className="relative">
        <button
          onClick={() => { setAvatarOpen(!avatarOpen); setStoreOpen(false); setLangOpen(false); }}
          className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
            AK
          </div>
          <div className="hidden sm:block text-left">
            <p className="text-xs font-semibold text-foreground leading-none">Ahmed Khan</p>
            <p className="text-2xs text-muted-foreground mt-0.5">Seller Pro</p>
          </div>
          <ChevronDown size={14} className="text-muted-foreground" />
        </button>

        {avatarOpen && (
          <div className="absolute top-full right-0 mt-1 w-52 bg-card border border-border rounded-xl shadow-panel py-1 fade-in">
            <div className="px-3 py-2 border-b border-border">
              <p className="text-sm font-semibold">Ahmed Khan</p>
              <p className="text-xs text-muted-foreground">ahmed.khan@gmail.com</p>
            </div>
            {[
              { icon: <User size={14} />, label: 'My Profile' },
              { icon: <Settings size={14} />, label: 'Account Settings' },
              { icon: <Store size={14} />, label: 'Store Settings' },
            ].map((item) => (
              <button
                key={`avatar-menu-${item.label}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
              >
                <span className="text-muted-foreground">{item.icon}</span>
                {item.label}
              </button>
            ))}
            <div className="border-t border-border mt-1 pt-1">
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-danger/5 transition-colors">
                <LogOut size={14} />
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}